'use client'

import { useEffect, useState } from 'react'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPlaybackPosition } from '@/lib/santa-chat/voice-duration'
import { SANTA_CHAT_INPUT_LIMITS } from '@/server/domain/santa-chat'

export interface RecordingHintLabels {
  recording: string
  slideToCancel: string
}

const RECORDING_TICK_MS = 250
/** Últimos segundos antes del corte automático: el contador pasa a cuenta atrás. */
export const RECORDING_WARNING_SECONDS = 5
const WARNING_VIBRATION_MS = 80

/** Segundos desde que monta (empieza a grabar), refrescados cuatro veces por segundo. */
function useElapsedSeconds(): number {
  const [startedAt] = useState(() => Date.now())
  const [now, setNow] = useState(startedAt)
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), RECORDING_TICK_MS)
    return () => clearInterval(timer)
  }, [])
  return (now - startedAt) / 1000
}

/** Un toque de vibración al entrar en los últimos segundos (Android; iOS no tiene la API). */
function useWarningVibration(warning: boolean): void {
  useEffect(() => {
    if (!warning) return
    try {
      navigator.vibrate?.(WARNING_VIBRATION_MS)
    } catch {
      // Sin permiso o sin API: la cuenta atrás ya avisa.
    }
  }, [warning])
}

/**
 * «● 0:04 ‹ Desliza para cancelar» y, debajo, una barra que se llena hasta el
 * corte automático. En los últimos 5 s el contador pasa a cuenta atrás grande
 * («5», «4»…) en `text-accent` y la barra también: el corte no pilla por sorpresa.
 */
export function RecordingHint({ labels }: { labels: RecordingHintLabels }) {
  const elapsed = useElapsedSeconds()
  const limit = SANTA_CHAT_INPUT_LIMITS.maxAudioSeconds
  const remaining = Math.max(0, limit - elapsed)
  const warning = remaining <= RECORDING_WARNING_SECONDS
  useWarningVibration(warning)
  return (
    <div className="readable-body relative flex h-12 flex-1 items-center justify-between overflow-hidden rounded-full bg-input px-4 text-sm">
      <span className={cn('flex items-center gap-2', warning ? 'text-accent' : 'text-destructive')}>
        <span aria-hidden className="size-2.5 rounded-full bg-current motion-safe:animate-pulse" />
        <span className="sr-only">{labels.recording}</span>
        {warning ? (
          <span data-testid="recording-countdown" aria-hidden className="text-lg font-bold tabular-nums motion-safe:animate-pulse">
            {Math.ceil(remaining)}
          </span>
        ) : (
          <span aria-hidden className="font-semibold tabular-nums">{formatPlaybackPosition(elapsed)}</span>
        )}
      </span>
      <span className="flex items-center text-muted-foreground">
        <ChevronLeft aria-hidden className="size-4" />
        {labels.slideToCancel}
      </span>
      <span
        aria-hidden
        data-testid="recording-progress"
        style={{ width: `${Math.min(1, elapsed / limit) * 100}%` }}
        className={cn(
          'absolute inset-x-0 bottom-0 h-1 transition-[width] duration-200 ease-linear',
          warning ? 'bg-accent' : 'bg-destructive/70'
        )}
      />
    </div>
  )
}
